<script lang="ts">
	import {word} from './stores';
	import Katex from './Katex.svelte';
	import numberWithCommas from './functions/numberWithCommas';
	import nCr from './functions/nCr'
	import fractionBuilder from './functions/fractionBuilder'
	import factorial from './functions/factorial'
	import Word from './functions/wordClass'
	
	let ai: string, aii: string, aiii: string, aiv: string, bi: string, bii: string;
	let denomString: string, denomNumber: number, distinct: number, more: number;

	let wordObject: Word;
	$: wordObject = new Word($word);
	$: denomString = denominatorToString(wordObject);
	$: denomNumber = denominatorToNumber(wordObject);
	$: ai = aiAnswer(wordObject);
	$: aii = aiiAnswer(wordObject);
	$: if (wordObject.doubles.length>0 || wordObject.triples.length>0) {
		aiii = aiiiAnswer(wordObject)
	};
	$: if (wordObject.vowelConsonantDiff < 2){
		aiv = aivAnswer(wordObject);
	}
	$: distinct = Object.keys(wordObject.lettersObject).length;
	$: more = wordObject.repeats.length - wordObject.doubles.length - wordObject.triples.length;
	$: bi = biAnswer(wordObject);
	$: bii = biiAnswer(wordObject);

	function aiAnswer(w:Word):string{
		if (denomString){
			return fractionBuilder(w.length.toString()+"!",denomString) + "=" + numberWithCommas(factorial(w.length)/denomNumber)
		} else{ // denom 1
			return w.length.toString() + "!=" + numberWithCommas(factorial(w.length)); 
		}
	}

	function aiiAnswer(w:Word):string{
		if (denomString){
			return fractionBuilder((w.length-2).toString()+"!",denomString) + "\\times {" + (w.length-1).toString() + "\\choose 2} \\times 2!=" + numberWithCommas(factorial(w.length-2)/denomNumber*2*nCr(w.length-1,2)); 
		} else {
			return (w.length-2).toString() + "!\\times {"+ (w.length-1).toString() + "\\choose 2}\\times 2!=" + numberWithCommas(factorial(w.length-2)*2*nCr(w.length-1,2));
		}
	}

	function aiiiAnswer(w:Word):string{
		let l = wordObject.length;
		let denomStringIII: string, denomNumberIII: number;
		if (w.triples.length>0){
			denomStringIII = denominatorToString(wordObject,3);
			denomNumberIII = denominatorToNumber(wordObject,3);
		} else{
			denomStringIII = denominatorToString(wordObject,2);
			denomNumberIII = denominatorToNumber(wordObject,2);
		}
		let answerString: string;
		if (denomStringIII){
			answerString = fractionBuilder((w.length-3).toString()+"!",denomStringIII);
		} else{
			answerString = (w.length-3) + "!";
		}
		if (wordObject.triples.length>0) {
			if (l % 2 == 1){
				answerString += "\\times 10=" + numberWithCommas(factorial(w.length-3)/denomNumberIII*10);
			} else {
				answerString += "\\times 4=" + numberWithCommas(factorial(w.length-3)/denomNumberIII*4);
			}
		} else{
			answerString += "\\times 3=" + numberWithCommas(factorial(w.length-2)/denomNumberIII*3);
		}
		return answerString;
	}

	function aivAnswer(w:Word):string{
		let answerString: string;
		if (denomString){
			answerString = fractionBuilder((w.length-w.vowelLength).toString() + "!" + w.vowelLength.toString() + "!",denomString);
		} else{
			answerString = (w.length-w.vowelLength).toString() + "!\\times " + w.vowelLength.toString() + "!";
		}
		if (wordObject.vowelConsonantDiff==1){
			return answerString + "=" + numberWithCommas(factorial(w.length-w.vowelLength)*factorial(w.vowelLength)/denomNumber)
		} else{
			return answerString + "\\times 2=" + numberWithCommas(factorial(w.length-w.vowelLength)*factorial(w.vowelLength)/denomNumber*2)
		}
	}

	function biAnswer(w:Word):string{
		let answerString = "{" + distinct.toString() + "\\choose 3} \\times 3!";
		let answer = nCr(distinct,3)*6
		if (w.doubles.length>0){
			answerString += "+ {" + (distinct-w.singles.length).toString() + "\\choose 1} {" + (distinct-1).toString() + "\\choose 1} \\times \\frac{3!}{2!}";
			answer += (distinct-w.singles.length) * (distinct-1) * 3;
		}
		if (w.triples.length>0){
			answerString += "+ {" + (w.triples.length+more).toString() + "\\choose 1}";
			answer += w.triples.length+more;
		}
		return answerString + "=" + numberWithCommas(answer);
	}

	function biiAnswer(w:Word):string{
		let answerString = ""; 
		let answer = 0;
		if (distinct > 3){
			answerString += "{" + distinct.toString() + "\\choose 4} \\times 4!";
			answer += nCr(distinct,4)*24;
		}
		if (w.repeats.length>0){
			answerString += "+ {" + (distinct-w.singles.length).toString() + "\\choose 1} {" + (distinct-1).toString() + "\\choose 2} \\times \\frac{4!}{2!}";
			answer += (distinct-w.singles.length) * nCr((distinct-1),2) * 12;
			if (w.repeats.length > 1){
				answerString += "+ {" + (distinct-w.singles.length).toString() + "\\choose 2} \\times \\frac{4!}{2!2!}";
				answer += nCr((distinct-w.singles.length),2) * 6;

			}
		}
		if (w.triples.length>0){
			answerString += "+ {" + (w.triples.length+more).toString() + "\\choose 1} {" + (distinct-1).toString() + "\\choose 1} \\times \\frac{4!}{3!}";
			answer += (w.triples.length+more)*(distinct-1)*4;
		}
		if (more){
			answerString += "+{" + more.toString() + "\\choose 1}";
			answer += more;
		}
		return answerString + "=" + numberWithCommas(answer);
	}

	function denominatorToString(w:Word,removed=0): string{
		let s = "";
		let firstTime = true;
		for (let letter of w.repeats) {
			if ( ( (removed==3 && w.triples.includes(letter)) || (removed==2 && w.doubles.includes(letter))) && firstTime){ // do nothing
				firstTime = false;
			} else{
				s += w.lettersObject[letter].toString() + "!";
			}
		}
		return s;
	}
	function denominatorToNumber(w:Word,removed=0): number{
		let n = 1;
		let firstTime = true;
		for (let letter of w.repeats) {
			if ( ( (removed==3 && w.triples.includes(letter)) || (removed==2 && w.doubles.includes(letter))) && firstTime){ // do nothing
				firstTime = false;
			} else{
				n *= factorial(w.lettersObject[letter]);
			}
		}
		return n;
	}
</script>

<div>
	<p>(ai) <Katex math={ai}/> </p>
	<p>(aii) <Katex math={aii}/> </p>
	{#if wordObject.doubles.length>0 || wordObject.triples.length>0}
		<p>(aiii) <Katex math={aiii}/> </p>
	{/if}
	{#if wordObject.vowelConsonantDiff < 2}
	<p>
		{#if wordObject.doubles.length >0 || wordObject.triples.length>0}
		(aiv)
		{:else}
		(aiii)
		{/if}
		<Katex math={aiv}/>
	</p>
	{/if}
	<p>(bi) <Katex math={bi}/> </p>
	{#if wordObject.length > 3}
		<p>(bii) <Katex math={bii}/> </p>
	{/if}
</div>